import { http } from '@pendo/http';
import {
    asCompetitorsString,
    normalizeBusinessContextCompanyURL,
} from './generate-business-context.utils.js';

export function getAutoProvisionFlagDisabledTooltip (type) {
    return {
        content: `${type} set via auto-provisioning and cannot be changed while auto-provisioning is enabled.`,
        multiline: true,
        arrow: true,
    };
}

export async function setMauLimit ({ mauLimit, subId }) {
    return http.put(`/api/super/s/${subId}/productline/core/metadata`, {
        mauLimit,
    });
}

export async function setAgentAnalyticsPromptLimits ({ monthlyPromptLimit, hourlyPromptLimit, subId }) {
    return http.put(`/api/super/s/${subId}/productline/agentAnalytics/metadata`, {
        monthlyPromptLimit,
        hourlyPromptLimit,
    });
}

export async function getAiBusinessContext ({ type = 'Subscription', id = '', subId = '_SID_' }) {
    return http
        .get(`/api/s/${subId}/businessContext/${type}/${id}`)
        .then((response) => response?.data || [])
        .catch((error) => {
            throw error;
        });
}

export async function getAiBusinessContextByType ({ type = 'Subscription', subId = '_SID_' }) {
    return http
        .get(`/api/s/${subId}/businessContext/${type}`)
        .then((response) => response?.data || [])
        .catch((error) => {
            throw error;
        });
}

export async function setAiBusinessContext ({ type = 'Subscription', id, subId = '_SID_', data }) {
    return http
        .post(`/api/s/${subId}/businessContext`, {
            type,
            parentId: id,
            formData: {
                body: data,
            },
        })
        .then(({ data }) => data)
        .catch((error) => {
            throw error;
        });
}

export async function updateAiBusinessContext ({ type = 'Subscription', id, subId = '_SID_', data }) {
    return http
        .patch(`/api/s/${subId}/businessContext/${type}/${id}`, {
            formData: {
                body: data,
            },
        })
        .then(({ data }) => data)
        .catch((error) => {
            throw error;
        });
}

export async function assessBusinessContextQuality ({ subId = '_SID_', data }) {
    return http
        .post(`/api/s/${subId}/businessContext/assessQuality`, data)
        .then(({ data }) => data)
        .catch((error) => {
            throw error;
        });
}

const EMPTY_GENERATE_BUSINESS_CONTEXT = {
    name: '',
    description: '',
    competitors: '',
    domain: '',
};

function hasGenerateBusinessContextFields (value) {
    return value != null
        && typeof value === 'object'
        && !Array.isArray(value)
        && ('name' in value || 'description' in value || 'competitors' in value || 'domain' in value);
}

function mapGenerateBusinessContextFields (raw) {
    return {
        name: String(raw.name ?? ''),
        description: typeof raw.description === 'string' ? raw.description : '',
        competitors: asCompetitorsString(raw.competitors),
        domain: raw.domain == null ? '' : String(raw.domain),
    };
}

/**
 * Normalizes POST `/api/s/{subId}/businessContext/generate` JSON (`response.data`)
 * into `{ name, description, competitors, domain }`.
 *
 * Supported shapes (first match wins):
 * - `{ formData: { body: { …fields } } }` — same as {@link setAiBusinessContext} / GET
 * - `{ formData: { …fields } }`
 * - `{ response: { …fields } }` or `{ response: { data: { …fields } } }`
 * - `{ data: { …fields } }`
 * - `{ name, description, competitors, domain }` — flat generate body
 *
 * @param {unknown} apiData
 * @returns {{ name: string, description: string, competitors: string, domain: string }}
 */
export function normalizeGenerateBusinessContextResponse (apiData) {
    if (apiData == null || typeof apiData !== 'object' || Array.isArray(apiData)) {
        return { ...EMPTY_GENERATE_BUSINESS_CONTEXT };
    }

    const responseWrapper = apiData.response && typeof apiData.response === 'object' && !Array.isArray(apiData.response)
        ? apiData.response
        : null;
    const dataWrapper = apiData.data && typeof apiData.data === 'object' && !Array.isArray(apiData.data)
        ? apiData.data
        : null;

    const candidates = [
        apiData.formData?.body,
        apiData.formData,
        responseWrapper?.data,
        responseWrapper,
        dataWrapper,
        apiData,
    ];

    for (const candidate of candidates) {
        if (hasGenerateBusinessContextFields(candidate)) {
            return mapGenerateBusinessContextFields(candidate);
        }
    }

    return { ...EMPTY_GENERATE_BUSINESS_CONTEXT };
}

export async function generateAiBusinessContext ({ subId = '_SID_', companyName, companyURL }) {
    const { data } = await http.post(`/api/s/${subId}/businessContext/generate`, {
        companyName,
        companyURL: normalizeBusinessContextCompanyURL(companyURL),
    });

    return normalizeGenerateBusinessContextResponse(data);
}

export const PENDO_INTERNAL_SUB_ID = '5668600916475904';

export const FY25_MODEL_STRING = 'FY25 Platform and Modules';

export const NOVUS_PRICING_MODEL = 'Novus';

/**
 * Handles common side effects for flag changes across subscription settings
 * @param {Object} params
 * @param {string} params.flag - The flag being changed
 * @param {boolean} params.value - The new value of the flag
 * @param {Function} params.handleFlagChange - Recursive handler for dependent flags
 */
export async function handleFlagSideEffects ({ flag, value, handleFlagChange }) {
    if (flag === 'pendoFree') {
        await handleFlagChange({ flag: 'enableWebHooks', value: !value });
    }

    if (flag === 'replayAiSummary') {
        window.pendo.track('Enabled Replay AI Summary', { enabled: value });
    }
}

export const DEFAULT_MIN_SEGMENT_SIZE = 5;

export const MIN_SEGMENT_SIZE_FLOOR = 1;

export const MIN_SEGMENT_SIZE_CEILING = 500;
